import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody, ModalHeader } from "reactstrap"
import { fetchAppointmentAttachmentUrl } from "helpers/api_helper"

const isImage = attachment =>
  String(attachment?.mimeType || "").startsWith("image/") ||
  /\.(jpe?g|png|webp)$/i.test(String(attachment?.fileName || ""))

const attachmentMessage = error => {
  const status = error?.response?.status
  if (status === 401 || status === 403) return "You do not have access to this attachment."
  if (status === 404) return "This attachment is no longer available."
  if (status === 503) return "Attachment preview is temporarily unavailable."
  return "Unable to load this attachment. Please try again."
}

const AttachmentItem = ({ appointmentId, attachment, index }) => {
  const attachmentId = attachment?._id || attachment?.id
  const image = isImage(attachment)
  const [thumbnailUrl, setThumbnailUrl] = useState("")
  const [previewUrl, setPreviewUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    let active = true
    setThumbnailUrl("")
    setPreviewUrl("")
    setError("")
    if (image && appointmentId && attachmentId) {
      fetchAppointmentAttachmentUrl(appointmentId, attachmentId)
        .then(url => {
          if (active && /^https:\/\//i.test(url || "")) setThumbnailUrl(url)
          else if (active) setError("Unable to load this attachment. Please try again.")
        })
        .catch(requestError => {
          if (active) setError(attachmentMessage(requestError))
        })
    }
    return () => { active = false }
  }, [appointmentId, attachmentId, image])

  const openAttachment = async () => {
    if (!appointmentId || !attachmentId) {
      setError("This attachment cannot be opened.")
      return
    }
    // Reserve a tab during the click only for files that use browser PDF handling.
    const fileTab = image ? null : window.open("", "_blank")
    setLoading(true)
    setError("")
    try {
      // Request a fresh URL for every open; thumbnail URLs may have expired.
      const url = await fetchAppointmentAttachmentUrl(appointmentId, attachmentId)
      if (!/^https:\/\//i.test(url || "")) throw new Error("Missing signed URL")
      if (image) {
        setPreviewUrl(url)
        setThumbnailUrl(url)
      } else if (fileTab) {
        fileTab.opener = null
        fileTab.location.replace(url)
      } else {
        setError("Your browser blocked the file tab. Please allow pop-ups and try again.")
      }
    } catch (requestError) {
      if (fileTab) fileTab.close()
      setError(attachmentMessage(requestError))
    } finally {
      setLoading(false)
    }
  }

  const fileName = attachment?.fileName || `Attachment ${index + 1}`
  return (
    <div className="mb-3">
      <button type="button" onClick={openAttachment} disabled={loading || !attachmentId}
        className="btn btn-light border text-start p-2 d-flex align-items-center gap-3"
        style={{ maxWidth: "100%", minWidth: 180 }} aria-label={`${image ? "Preview" : "Open"} ${fileName}`}>
        {image && thumbnailUrl ? (
          <img src={thumbnailUrl} alt="" style={{ width: 88, height: 72, objectFit: "cover", borderRadius: 4 }}
            onError={() => { setThumbnailUrl(""); setError("Image preview unavailable. Click to try again.") }} />
        ) : <span className="d-flex align-items-center justify-content-center bg-white border rounded"
          style={{ width: 88, height: 72, flexShrink: 0 }}>{image ? "Image" : "PDF / File"}</span>}
        <span style={{ overflowWrap: "anywhere" }}>{fileName}<br />
          <small className="text-muted">{loading ? "Opening..." : image ? "Click to enlarge" : "Click to open"}</small>
        </span>
      </button>
      {error && <div role="alert" className="text-danger mt-1">{error}</div>}
      <Modal isOpen={Boolean(previewUrl)} toggle={() => setPreviewUrl("")} size="xl" centered>
        <ModalHeader toggle={() => setPreviewUrl("")}>{fileName}</ModalHeader>
        <ModalBody className="text-center">
          {previewUrl && <img src={previewUrl} alt={fileName}
            style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
            onError={() => { setPreviewUrl(""); setError("Image preview unavailable. Click to try again.") }} />}
        </ModalBody>
      </Modal>
    </div>
  )
}

AttachmentItem.propTypes = {
  appointmentId: PropTypes.string,
  attachment: PropTypes.object,
  index: PropTypes.number,
}

const AppointmentAttachments = ({ appointmentId, attachments }) => {
  const items = Array.isArray(attachments) ? attachments : []
  if (!items.length) return <span className="text-muted">Not provided</span>
  return items.map((attachment, index) => (
    <AttachmentItem key={attachment?._id || attachment?.id || index}
      appointmentId={appointmentId} attachment={attachment} index={index} />
  ))
}

AppointmentAttachments.propTypes = {
  appointmentId: PropTypes.string,
  attachments: PropTypes.array,
}

export default AppointmentAttachments
